import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useNavigate, useParams } from 'react-router'
import { z } from 'zod'

import {
  adminCreateDeviceMutation,
  adminCreateParentMutation,
  adminDeleteDeviceMutation,
  adminDeleteFamilyMutation,
  adminDeleteParentMutation,
  adminFamilyDetailOptions,
  adminFamilyDetailQueryKey,
  adminListFamiliesQueryKey,
  adminUpdateDeviceMutation,
  adminUpdateFamilyMutation,
  adminUpdateParentMutation,
} from '@/client/@tanstack/react-query.gen'
import type { AccountRead, DeviceRead } from '@/client'
import { DeviceOnlineIndicator } from '@/components/features/device-online-indicator'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

const STATUSES = ['active', 'trial', 'inactive'] as const

const parentSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.email('Enter a valid email'),
  password: z.string().min(8, 'At least 8 characters'),
})
type ParentValues = z.infer<typeof parentSchema>

const deviceSchema = z.object({
  name: z.string().min(1, 'Required'),
  serial_number: z.string().min(1, 'Required'),
})
type DeviceValues = z.infer<typeof deviceSchema>

const familyNameSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
})
type FamilyNameValues = z.infer<typeof familyNameSchema>

const parentEditSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.email('Enter a valid email'),
  password: z.union([z.literal(''), z.string().min(8, 'At least 8 characters')]),
})
type ParentEditValues = z.infer<typeof parentEditSchema>

function AddParentDialog({ familyId, onDone }: { familyId: string; onDone: () => void }) {
  const [open, setOpen] = useState(false)
  const form = useForm<ParentValues>({
    resolver: zodResolver(parentSchema),
    defaultValues: { name: '', email: '', password: '' },
  })
  const create = useMutation({
    ...adminCreateParentMutation(),
    onSuccess: () => {
      onDone()
      setOpen(false)
      form.reset()
    },
  })

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">Add parent</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add parent</DialogTitle>
          <DialogDescription>Create a Parent account in this family.</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit((values) =>
              create.mutate({ path: { family_id: familyId }, body: values }),
            )}
            noValidate
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input type="email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <Input type="password" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? 'Creating…' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

function AddDeviceDialog({ familyId, onDone }: { familyId: string; onDone: () => void }) {
  const [open, setOpen] = useState(false)
  const form = useForm<DeviceValues>({
    resolver: zodResolver(deviceSchema),
    defaultValues: { name: '', serial_number: '' },
  })
  const create = useMutation({
    ...adminCreateDeviceMutation(),
    onSuccess: () => {
      onDone()
      setOpen(false)
      form.reset()
    },
  })

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">Add device</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add device</DialogTitle>
          <DialogDescription>Enroll a device by its serial number.</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit((values) =>
              create.mutate({ path: { family_id: familyId }, body: values }),
            )}
            noValidate
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="serial_number"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Serial number</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? 'Creating…' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

function RemoveParentButton({
  familyId,
  parentId,
  name,
  onDone,
}: {
  familyId: string
  parentId: string
  name: string
  onDone: () => void
}) {
  const remove = useMutation({ ...adminDeleteParentMutation(), onSuccess: onDone })
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="ghost" size="sm">
          Remove
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Remove {name}?</AlertDialogTitle>
          <AlertDialogDescription>
            This permanently deletes the parent account.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => remove.mutate({ path: { family_id: familyId, parent_id: parentId } })}
          >
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

function RenameFamilyDialog({
  familyId,
  name,
  onDone,
}: {
  familyId: string
  name: string
  onDone: () => void
}) {
  const [open, setOpen] = useState(false)
  const form = useForm<FamilyNameValues>({
    resolver: zodResolver(familyNameSchema),
    defaultValues: { name },
  })
  const update = useMutation({
    ...adminUpdateFamilyMutation(),
    onSuccess: () => {
      onDone()
      setOpen(false)
    },
  })

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          Rename
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rename family</DialogTitle>
          <DialogDescription>Change the family's display name.</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit((values) =>
              update.mutate({ path: { family_id: familyId }, body: { name: values.name } }),
            )}
            noValidate
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="submit" disabled={update.isPending}>
                {update.isPending ? 'Saving…' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

function DeleteFamilyButton({ familyId, name }: { familyId: string; name: string }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [confirm, setConfirm] = useState('')
  const remove = useMutation({
    ...adminDeleteFamilyMutation(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminListFamiliesQueryKey() })
      void navigate('/admin/families')
    },
  })

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) setConfirm('')
      }}
    >
      <DialogTrigger asChild>
        <Button variant="destructive" size="sm">
          Delete family
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete {name}?</DialogTitle>
          <DialogDescription>
            This permanently deletes the family and cascades to its parents, devices, and all
            captured activity. This cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="confirm-family-name">
            Type <span className="font-medium">{name}</span> to confirm
          </Label>
          <Input
            id="confirm-family-name"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </div>
        <DialogFooter>
          <Button
            variant="destructive"
            disabled={confirm !== name || remove.isPending}
            onClick={() => remove.mutate({ path: { family_id: familyId } })}
          >
            {remove.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function EditParentDialog({
  familyId,
  parent,
  onDone,
}: {
  familyId: string
  parent: AccountRead
  onDone: () => void
}) {
  const [open, setOpen] = useState(false)
  const form = useForm<ParentEditValues>({
    resolver: zodResolver(parentEditSchema),
    defaultValues: { name: parent.name, email: parent.email, password: '' },
  })
  const update = useMutation({
    ...adminUpdateParentMutation(),
    onSuccess: () => {
      onDone()
      setOpen(false)
    },
  })

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          Edit
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit parent</DialogTitle>
          <DialogDescription>
            Update the account details. Leave password blank to keep the current one.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit((values) =>
              update.mutate({
                path: { family_id: familyId, parent_id: parent.id },
                body: {
                  name: values.name,
                  email: values.email,
                  ...(values.password ? { password: values.password } : {}),
                },
              }),
            )}
            noValidate
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input type="email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <Input type="password" placeholder="Leave blank to keep current" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="submit" disabled={update.isPending}>
                {update.isPending ? 'Saving…' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

function EditDeviceDialog({
  familyId,
  device,
  onDone,
}: {
  familyId: string
  device: DeviceRead
  onDone: () => void
}) {
  const [open, setOpen] = useState(false)
  const form = useForm<DeviceValues>({
    resolver: zodResolver(deviceSchema),
    defaultValues: { name: device.name, serial_number: device.serial_number },
  })
  const update = useMutation({
    ...adminUpdateDeviceMutation(),
    onSuccess: () => {
      onDone()
      setOpen(false)
    },
  })

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          Edit
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit device</DialogTitle>
          <DialogDescription>Rename the device or correct its serial number.</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit((values) =>
              update.mutate({ path: { family_id: familyId, device_id: device.id }, body: values }),
            )}
            noValidate
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="serial_number"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Serial number</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="submit" disabled={update.isPending}>
                {update.isPending ? 'Saving…' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

function RemoveDeviceButton({
  familyId,
  deviceId,
  name,
  onDone,
}: {
  familyId: string
  deviceId: string
  name: string
  onDone: () => void
}) {
  const remove = useMutation({ ...adminDeleteDeviceMutation(), onSuccess: onDone })
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="ghost" size="sm">
          Remove
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Remove {name}?</AlertDialogTitle>
          <AlertDialogDescription>
            This permanently deletes the device and its captured data.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => remove.mutate({ path: { family_id: familyId, device_id: deviceId } })}
          >
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export function AdminFamilyDetail() {
  const { familyId = '' } = useParams()
  const queryClient = useQueryClient()
  const family = useQuery(adminFamilyDetailOptions({ path: { family_id: familyId } }))
  const refetch = (): void => {
    void queryClient.invalidateQueries({
      queryKey: adminFamilyDetailQueryKey({ path: { family_id: familyId } }),
    })
  }
  const updateFamily = useMutation({ ...adminUpdateFamilyMutation(), onSuccess: refetch })

  if (!family.data) {
    return <p className="text-muted-foreground">Loading…</p>
  }
  const { name, subscription_status, parents, devices, created_at, updated_at } = family.data

  return (
    <div className="mx-auto max-w-4xl space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{name}</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Created {new Date(created_at).toLocaleDateString()} · Updated{' '}
            {new Date(updated_at).toLocaleDateString()}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <RenameFamilyDialog familyId={familyId} name={name} onDone={refetch} />
          <DeleteFamilyButton familyId={familyId} name={name} />
          <label className="text-sm">
            <span className="text-muted-foreground mr-2">Status</span>
            <select
              value={subscription_status}
              onChange={(e) =>
                updateFamily.mutate({
                  path: { family_id: familyId },
                  body: { subscription_status: e.target.value as (typeof STATUSES)[number] },
                })
              }
              className="border-input bg-background rounded-md border px-2 py-1 text-sm"
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Parents</h2>
          <AddParentDialog familyId={familyId} onDone={refetch} />
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="w-0" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {parents.map((parent) => (
              <TableRow key={parent.id}>
                <TableCell className="font-medium">{parent.name}</TableCell>
                <TableCell>{parent.email}</TableCell>
                <TableCell className="text-muted-foreground">
                  {new Date(parent.created_at).toLocaleDateString()}
                </TableCell>
                <TableCell className="text-right space-x-1">
                  <EditParentDialog familyId={familyId} parent={parent} onDone={refetch} />
                  <RemoveParentButton
                    familyId={familyId}
                    parentId={parent.id}
                    name={parent.name}
                    onDone={refetch}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </section>

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Devices</h2>
          <AddDeviceDialog familyId={familyId} onDone={refetch} />
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Serial</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="w-0" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {devices.map((device) => (
              <TableRow key={device.id}>
                <TableCell className="font-medium">
                  <span className="flex items-center gap-2">
                    <DeviceOnlineIndicator
                      lastHeartbeatAt={device.last_heartbeat_at}
                      userAgentHeartbeatAt={device.last_user_agent_heartbeat_at}
                      showLabel={false}
                    />
                    <Link
                      to={`/admin/families/${familyId}/devices/${device.id}`}
                      className="hover:underline"
                    >
                      {device.name}
                    </Link>
                  </span>
                </TableCell>
                <TableCell>{device.serial_number}</TableCell>
                <TableCell className="capitalize">{device.status}</TableCell>
                <TableCell className="text-muted-foreground">
                  {new Date(device.created_at).toLocaleDateString()}
                </TableCell>
                <TableCell className="text-right space-x-1">
                  <EditDeviceDialog familyId={familyId} device={device} onDone={refetch} />
                  <RemoveDeviceButton
                    familyId={familyId}
                    deviceId={device.id}
                    name={device.name}
                    onDone={refetch}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </section>
    </div>
  )
}
